import React, { useEffect, useState } from "react";
import { MapPin, Phone, Clock, Send, ShieldAlert, Loader2 } from "lucide-react";
import { ORG } from "../mock";
import { useToast } from "../hooks/use-toast";

// Netlify Forms: submissions are url-encoded and posted to the site root.
// They are captured by Netlify (no backend) and viewable in the Netlify dashboard.
const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
    .join("&");

export default function Contact() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", subject: "", type: "General Enquiry", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete the required fields", description: "Name, email and message are required." });
      return;
    }
    setSubmitting(true);
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", "bot-field": "", ...form }),
      });
    } catch (err) {
      // Submission is handled by Netlify in production; ignore preview network errors.
    }
    toast({ title: "Inquiry submitted", description: "Thank you. The Commission has received your inquiry and will respond shortly." });
    setForm({ name: "", email: "", subject: "", type: "General Enquiry", message: "" });
    setSubmitting(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <section className="hero-pattern text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="font-serif text-3xl sm:text-5xl font-bold mb-4">Contact the Commission</h1>
          <p className="text-slate-200 max-w-2xl text-lg">
            Send us an inquiry using the form below for general questions, licensing matters, or to
            report suspected misconduct and unlicensed activity.
          </p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
          {/* Info */}
          <div className="lg:col-span-1 space-y-5">
            {[
              { Icon: MapPin, title: "Head Office", value: ORG.address },
              { Icon: Phone, title: "Telephone", value: `${ORG.phone} \u00b7 ${ORG.altPhone}` },
              { Icon: Clock, title: "Office Hours", value: ORG.hours },
            ].map((c) => (
              <div key={c.title} className="flex gap-4 bg-slate-50 rounded-xl border border-slate-200 p-5">
                <div className="w-11 h-11 rounded-lg bg-brand-navy flex items-center justify-center shrink-0">
                  <c.Icon className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-brand-navy text-sm">{c.title}</h3>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">{c.value}</p>
                </div>
              </div>
            ))}
            <div className="flex gap-4 bg-[#eaf4ee] rounded-xl border border-green-200 p-5">
              <ShieldAlert className="text-brand-green shrink-0" size={22} />
              <p className="text-sm text-slate-600">
                To report fraud or an unlicensed firm, select <span className="font-semibold text-brand-navy">&quot;Report Misconduct&quot;</span> in the inquiry form and describe the matter.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <form
              name="contact"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              onSubmit={submit}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p hidden>
                <label>Do not fill this out: <input name="bot-field" /></label>
              </p>
              <h2 className="font-serif text-2xl font-bold text-brand-navy mb-1">Send an inquiry</h2>
              <p className="text-sm text-slate-500 mb-6">All inquiries are received directly by the Commission.</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">Full name *</label>
                  <input value={form.name} onChange={set("name")} className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand-green focus:ring-2 focus:ring-green-100 outline-none text-sm" placeholder="Your name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">Email address *</label>
                  <input type="email" value={form.email} onChange={set("email")} className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand-green focus:ring-2 focus:ring-green-100 outline-none text-sm" placeholder="you@example.com" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">Inquiry type</label>
                  <select value={form.type} onChange={set("type")} className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand-green focus:ring-2 focus:ring-green-100 outline-none text-sm bg-white">
                    <option>General Enquiry</option>
                    <option>Report Misconduct</option>
                    <option>Licensing Question</option>
                    <option>Media / Press</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">Subject</label>
                  <input value={form.subject} onChange={set("subject")} className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand-green focus:ring-2 focus:ring-green-100 outline-none text-sm" placeholder="Subject" />
                </div>
              </div>
              <div className="mt-5">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">Message *</label>
                <textarea value={form.message} onChange={set("message")} rows={5} className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand-green focus:ring-2 focus:ring-green-100 outline-none text-sm resize-none" placeholder="How can we help?" />
              </div>
              <button type="submit" disabled={submitting} className="mt-6 inline-flex items-center gap-2 bg-brand-green text-white font-semibold px-6 py-3.5 rounded-md hover:bg-[#0f6339] transition-colors disabled:opacity-70">
                {submitting ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                {submitting ? "Submitting..." : "Submit inquiry"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
